/**
 * `06/ Billing` (Figma 12:5634) — `/countries/:cc/billing`, v19 D4. THE HONEST SUBSET.
 *
 * There is NO invoice entity in this product (proposal §8 #11). What exists is billable
 * documents (deployed, signed off, billed, closed), their `payment_status`
 * (advance / pending / received / foc), `amount`, `advance_amount`, and the linked sales
 * order's invoice number/date when one exists. So that is what this report shows — and the
 * four Figma KPIs that CANNOT exist (outstanding, received this month, overdue invoices,
 * average days to payment) are NOT drawn: no column, no tile, no zero. The server SERVES
 * that statement in `disclosures[]` and it is rendered under the table; `reports.test.tsx`
 * asserts no column header or tile names a due date, an overdue or a days-to-payment.
 *
 * `billing.view` (Country Manager, Coordinator, Accounts); export `reports.export`. Money
 * masked per row; the by-payment-status sums are masked per cell (each carries
 * `country_code`).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { CRF_STAGES, WORK_TYPE_LABEL } from '../crfs/domain';
import { KpiTile } from '../dashboards/dataviz';
import { formatDateLong, formatMoney } from '../inquiries/format';
import { ServerAmount } from '../quotes/money';
import { formatDateOnly } from '../renewals/format';
import { PAYMENT_LABEL } from '../work-pipeline/payment-status';
import type { PaymentStatus } from '../work-pipeline/api';
import { fetchBillingReport, reportKeys } from './api';
import type { BillingReportDto, BillingRowDto } from './api';
import { ReportFrame } from './ReportFrame';
import type { ReportColumn } from './ReportFrame';
import { useReportUrl } from './report-filters';

function paymentLabel(status: string | null): string {
  if (status === null) return 'No payment status';
  return (PAYMENT_LABEL as Readonly<Record<string, string>>)[status as PaymentStatus] ?? status;
}

function columnsFor(countryCode: string): readonly ReportColumn<BillingRowDto>[] {
  return [
    {
      key: 'doc',
      header: 'Document',
      width: 'w-[170px]',
      cell: (row) => (
        <Link
          to={`/countries/${countryCode}/crfs/${row.crf_id}`}
          className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
        >
          {row.doc_no}
        </Link>
      ),
      csv: (row) => row.doc_no,
    },
    { key: 'type', header: 'Work type', width: 'w-[90px]', cell: (row) => WORK_TYPE_LABEL[row.work_type], csv: (row) => WORK_TYPE_LABEL[row.work_type] },
    { key: 'party', header: 'Customer', cell: (row) => row.party.name, csv: (row) => row.party.name },
    { key: 'status', header: 'Stage', width: 'w-[130px]', cell: (row) => CRF_STAGES[row.status]?.label ?? row.status, csv: (row) => CRF_STAGES[row.status]?.label ?? row.status },
    { key: 'payment', header: 'Payment status', width: 'w-[150px]', cell: (row) => paymentLabel(row.payment_status), csv: (row) => paymentLabel(row.payment_status) },
    {
      key: 'amount',
      header: 'Amount',
      width: 'w-[130px]',
      numeric: true,
      cell: (row) => <ServerAmount value={row.amount} currency={row.currency} />,
      csv: (row) => formatMoney(row.amount, row.currency),
    },
    {
      key: 'advance',
      header: 'Advance',
      width: 'w-[130px]',
      numeric: true,
      cell: (row) => <ServerAmount value={row.advance_amount} currency={row.currency} />,
      csv: (row) => formatMoney(row.advance_amount, row.currency),
    },
    {
      key: 'invoice',
      header: 'Invoice (SO)',
      width: 'w-[190px]',
      cell: (row) =>
        row.invoice_no === null ? (
          <span className="text-fg-secondary">—</span>
        ) : (
          <span>
            <span className="font-mono text-mono">{row.invoice_no}</span>
            {row.invoice_date === null ? '' : <span className="text-fg-secondary"> · {formatDateOnly(row.invoice_date)}</span>}
            {row.so_doc_no === null ? '' : <span className="text-fg-secondary"> · {row.so_doc_no}</span>}
          </span>
        ),
      csv: (row) =>
        row.invoice_no === null
          ? '—'
          : [row.invoice_no, row.invoice_date === null ? '' : formatDateOnly(row.invoice_date), row.so_doc_no ?? ''].filter((part) => part !== '').join(' · '),
    },
    { key: 'since', header: 'Billable since', width: 'w-[130px]', cell: (row) => formatDateLong(row.billable_since), csv: (row) => formatDateLong(row.billable_since) },
  ];
}

function Summary({ data }: { data: BillingReportDto }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="billing-summary">
      <KpiTile label="Billable" value={data.summary.billable} detail="First deployed in this window" />
      <KpiTile
        label="Without a clock"
        value={data.summary.unstamped}
        detail="Billable status, no deployment row — not in any window"
      />
      {data.summary.by_payment_status.map((cell) => (
        <KpiTile
          key={cell.payment_status ?? 'none'}
          testId={`billing-${cell.payment_status ?? 'none'}`}
          label={paymentLabel(cell.payment_status)}
          value={cell.count}
          detail={cell.amount === null ? 'No amount recorded' : <ServerAmount value={cell.amount} currency={data.currency} />}
        />
      ))}
    </div>
  );
}

export function BillingReportPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const canView = can(countryCode, 'billing.view');
  const canExport = can(countryCode, 'reports.export');
  const { filters } = useReportUrl();

  const query = useQuery({
    queryKey: reportKeys.billing(countryCode, filters),
    queryFn: ({ signal }) => fetchBillingReport(countryCode, filters, signal),
    enabled: canView,
  });
  const columns = useMemo(() => columnsFor(countryCode), [countryCode]);

  return (
    <ReportFrame
      title="Billing"
      testId="report-billing"
      refusal={
        canView
          ? null
          : {
              title: 'Billing is not available to this account',
              body: `Viewing billing in ${countryCode} needs the billing permission. Ask an administrator to widen your access.`,
            }
      }
      query={query}
      columns={columns}
      rowKey={(row) => row.crf_id}
      noun={['billable document', 'billable documents']}
      summary={(data) => <Summary data={data as BillingReportDto} />}
      canExport={canExport}
      filename={`${countryCode.toLowerCase()}-report-billing.csv`}
      emptyTitle="Nothing became billable in this window."
    />
  );
}
